'use client';
import React, { useMemo } from 'react';
import { useModel } from '@/context/ModelContext';
import { computeModel, Assumptions } from '@/lib/financial-model';

const PRICES = [7000, 7500, 8000, 8500, 9000, 9500, 10000];
const UTILS = [0.55, 0.65, 0.75, 0.85, 0.95, 1.0];

export default function SensitivityHeatmap() {
  const { assumptions } = useModel();

  // Compute the grid of IRRs
  const grid = useMemo(() => {
    return PRICES.map((price) => {
      return UTILS.map((util) => {
        const testAssumptions: Assumptions = {
          ...assumptions,
          sellingPrice: price,
          year2PlusUtilisation: util,
        };
        const model = computeModel(testAssumptions);
        return model.projectIRR;
      });
    });
  }, [assumptions]);

  // Tailwind class mapping based on IRR
  const getColorClass = (irr: number) => {
    if (irr < 0.15) return 'bg-red-500/40 text-white border border-red-500/50';
    if (irr < 0.25) return 'bg-yellow-500/40 text-white border border-yellow-500/50';
    return 'bg-emerald-500/40 text-white border border-emerald-500/50';
  };

  return (
    <div className="exec-panel">
      <div className="exec-panel-header">
        <h2
          style={{
            fontSize: '0.75rem',
            fontWeight: 700,
            color: 'var(--text-primary)',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}
        >
          Project IRR Sensitivity Heatmap
        </h2>
        <p style={{ fontSize: '0.65rem', color: 'var(--text-tertiary)', marginTop: 2 }}>
          Selling Price vs Steady-State Utilisation
        </p>
      </div>
      <div className="p-5 overflow-x-auto">
        <div style={{ minWidth: '600px' }}>
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="p-2 text-right">
                  <span style={{ fontSize: '0.6rem', color: 'var(--text-tertiary)', textTransform: 'uppercase' }}>
                    Price \ Utilisation →
                  </span>
                </th>
                {UTILS.map((u) => (
                  <th key={u} className="p-2 text-center" style={{ borderBottom: '1px solid var(--divider)' }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-secondary)' }}>
                      {(u * 100).toFixed(0)}%
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PRICES.map((price, rIdx) => (
                <tr key={price}>
                  <td className="p-2 text-right" style={{ borderRight: '1px solid var(--divider)', width: '120px' }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-secondary)' }}>
                      ₹{price.toLocaleString('en-IN')}/t
                    </span>
                  </td>
                  {grid[rIdx].map((irr, cIdx) => (
                    <td key={cIdx} className="p-1">
                      <div
                        className={`w-full h-10 flex items-center justify-center rounded transition-all hover:scale-105 shadow-sm ${getColorClass(irr)}`}
                        style={{ cursor: 'pointer' }}
                        title={`Price: ₹${price}, Util: ${(UTILS[cIdx] * 100).toFixed(0)}%, IRR: ${(irr * 100).toFixed(1)}%`}
                      >
                        <span
                          className="font-mono"
                          style={{
                            fontSize: '0.75rem',
                            fontWeight: 800,
                            textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                          }}
                        >
                          {(irr * 100).toFixed(1)}%
                        </span>
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
